/* eslint-disable camelcase */
import * as d3 from "d3";
import { RawData } from "./series_graph";
import { SeriesExerciseGraph } from "./series_exercise_graph";
import { themeState } from "state/Theme";
import { i18n } from "i18n/i18n";

interface ExerciseData {
    date: Date;
    sum: number;
    [index: string]: number | Date;
}

interface TimeseriesData {
    ex_id: string;
    ex_data: ExerciseData[];
}

export class TimeseriesGraph extends SeriesExerciseGraph {
    protected readonly baseUrl = "/stats/timeseries?series_id=";

    /**
     * Constructs a TimeseriesGraph instance.
     *
     * @param {string} seriesId - The series identifier.
     * @param {string} containerId - The container's HTML identifier.
     * @param {RawData} [data] - Optional raw data for the graph.
     */
    constructor(seriesId: string, containerId: string, data?: RawData) {
        super(seriesId, containerId, data);
        this.margin.right = 40;
    }

    // Axes scales
    private x: d3.ScaleBand<Date>;
    private color: d3.ScaleSequential<string>;

    // Data parameters
    private maxBinValue = 0; // Largest bin value.
    private minDate: Date;
    private maxDate: Date;
    private binTicks: Date[] = [];
    private binStepSize: number;
    private data: TimeseriesData[] = [];

    // SVG elements
    private tooltip: d3.Selection<HTMLDivElement, unknown, HTMLElement, unknown>;

    /**
     * Draws the graph's SVG elements on the screen.
     *
     * @param {boolean} animate - Whether to animate the drawing (disabled on resize redraw).
     */
    protected override draw(animate: boolean = true): void {
        // If not enough bin ticks exist, display a "no data" view.
        if (this.binTicks.length < 2) {
            this.drawNoData();
            return;
        }

        super.draw(animate);

        // Retrieve theme colors.
        const emptyColor = themeState.getCSSVariable("--d-off-surface");
        const highColor = themeState.getCSSVariable("--d-primary");
        const lowColor = d3.interpolateRgb(emptyColor, highColor)(0.2);

        // Adjust endDate for proper binning.
        const endDate = new Date(this.maxDate);
        endDate.setHours(0, 0, 0, 0);

        // Set up sequential color scale.
        this.color = d3.scaleSequential(d3.interpolate(lowColor, highColor)).domain([0, this.maxBinValue]);

        // Define x-axis band scale.
        this.x = d3.scaleBand<Date>().domain(this.binTicks).range([0, this.innerWidth]);

        // Append x-axis with custom tick formatting.
        this.graph
            .append("g")
            .attr("transform", `translate(0, ${this.innerHeight - this.y.bandwidth() / 2})`)
            .call(
                d3
                    .axisBottom(this.x)
                    .tickValues(this.binTicks.slice(0, this.binTicks.length - 1))
                    .tickFormat((t: Date) => {
                        const asDate = new Date(t);
                        const timeZoneDiff = (asDate.getTimezoneOffset() - this.minDate.getTimezoneOffset()) / 60;
                        if (
                            this.binStepSize >= 24 ||
                            (asDate.getHours() === (24 - timeZoneDiff) % 24 && asDate.getMinutes() === 0)
                        ) {
                            return d3.timeFormat(i18n.t("date.formats.weekday_short"))(t);
                        } else {
                            return d3.timeFormat(i18n.t("time.formats.plain_time"))(t);
                        }
                    }),
            )
            .selectAll("text")
            .attr("dy", ".7em")
            .attr("transform", "rotate(-25)")
            .style("text-anchor", "end");

        this.graph.select(".domain").remove();

        // Initialize tooltip.
        this.tooltip = this.container
            .append("div")
            .attr("class", "d3-tooltip")
            .attr("pointer-events", "none")
            .style("opacity", 0)
            .style("z-index", 5);

        // Determine rectangle size.
        const rectSize = Math.min(this.y.bandwidth() * 1.5, this.innerWidth / this.binTicks.length - 5);

        // Draw rectangles for each timeseries data record.
        this.graph
            .selectAll(".rectGroup")
            .data(this.data)
            .join("g")
            .attr("class", "rectGroup")
            .each(({ ex_data, ex_id }: TimeseriesData, i, group) => {
                d3.select(group[i])
                    .selectAll("rect")
                    .data(ex_data, (d: ExerciseData) => "" + d.date)
                    .join("rect")
                    .attr("class", "day-cell")
                    .classed("empty", (d) => d.sum === 0)
                    .attr("x", (d: ExerciseData) => this.x(d.date) + (this.x.bandwidth() - rectSize) / 2)
                    .attr("y", () => this.y(ex_id) + (this.y.bandwidth() - rectSize) / 2)
                    .attr("rx", 6)
                    .attr("ry", 6)
                    .attr("fill", emptyColor)
                    .attr("stroke", highColor)
                    .on("mouseover", (_e, d: ExerciseData) => this.tooltipHover(d))
                    .on("mousemove", (e) => this.tooltipMove(e))
                    .on("mouseout", () => this.tooltipOut())
                    .transition()
                    .duration(animate ? 500 : 0)
                    .attr("width", rectSize)
                    .attr("height", rectSize)
                    .attr("fill", (d: ExerciseData) => (d.sum === 0 ? "" : this.color(d.sum)));
            });

        // Calculate time units for the legend.
        const unitSizes = [60, 60, 24, 7];
        const units = ["sec", "min", "hour", "day", "week"];
        let step = this.binStepSize * 3600;
        let i = 0;
        while (i < unitSizes.length && step / unitSizes[i] >= 1) {
            step /= unitSizes[i];
            i++;
        }

        // Create legend element.
        const legend = this.container
            .append("div")
            .style("position", "absolute")
            .style("top", `${this.innerHeight}px`)
            .append("div")
            .style("display", "flex")
            .style("align-items", "center");

        legend
            .append("div")
            .style("border-radius", "3px")
            .style("height", "20px")
            .style("width", "20px")
            .style("border-style", "solid")
            .style("border-width", "2px")
            .style("border-color", highColor);

        legend
            .append("span")
            .text(i18n.t(`time.units.${units[i]}`, { smart_count: step }))
            .attr("class", "legend-text")
            .style("margin-left", "5px");
    }

    /**
     * Transforms raw server data into a format usable by the timeseries graph.
     *
     * @param {RawData} raw - The raw data containing exercise timeseries records.
     */
    protected override processData({ data, exercises, deadline }: RawData): void {
        type Datum = {
            date: Date | string;
            status: string;
            count: number;
        };

        // Parse exercises based on provided exercise IDs.
        this.parseExercises(
            exercises,
            data.map((exercise) => exercise.ex_id),
        );

        // Convert date strings to Date objects.
        data.forEach((exercise) => {
            exercise.ex_data.forEach((d: Datum) => {
                d.date = new Date(d.date);
            });
        });

        // Determine minimum and maximum dates across all data.
        let [minDate, maxDate] = d3.extent(
            data.flatMap((ex) => ex.ex_data),
            (d: Datum) => d.date as Date,
        );

        if (deadline) {
            maxDate = deadline;
        }

        // Set graph boundaries.
        this.minDate = this.dateStart ? new Date(this.dateStart) : new Date(minDate);
        this.maxDate = this.dateEnd ? new Date(this.dateEnd) : new Date(maxDate);

        // Determine bin step size, ticks, and aligned start.
        const [binStep, binTicks, alignedStart] = this.findBinTime(this.minDate, this.maxDate, 17);
        this.binStepSize = binStep;
        this.binTicks = binTicks as unknown as Date[];
        this.minDate = alignedStart;
        this.maxDate = new Date(this.binTicks[this.binTicks.length - 1]);

        if (this.binTicks.length < 2) return;

        if (!this.dateStart) {
            this.setPickerDates(this.minDate, this.maxDate);
        }

        // Process each exercise's data.
        this.data = [];
        this.maxBinValue = 0;
        data.forEach(({ ex_id, ex_data }) => {
            let binned = d3
                .bin<Datum, number>()
                .value((d) => (d.date as Date).getTime())
                .thresholds(binTicks as number[])
                .domain([this.minDate.getTime(), this.maxDate.getTime()])(ex_data as Datum[]);

            // Remove the 'before' bin if present.
            if (binned[0].x0 < this.minDate.getTime()) {
                binned = binned.slice(1);
            }
            const binAmount = binned.length;
            // Remove the last bin if its start and end times are equal.
            if (binned[binAmount - 1].x0 === binned[binAmount - 1].x1) {
                binned.pop();
            }

            const parsedData: ExerciseData[] = [];
            // Aggregate each bin into a single record.
            binned.forEach((bin) => {
                const sum = d3.sum(bin, (r) => r.count);
                this.maxBinValue = Math.max(this.maxBinValue, sum);

                const record: ExerciseData = {
                    date: new Date(bin.x0),
                    sum: sum,
                };

                // Initialize all status counts to 0.
                this.statusOrder.forEach((status) => {
                    record[status] = 0;
                });

                // Sum counts by status.
                bin.forEach((r) => {
                    record[r.status] = ((record[r.status] as number) || 0) + r.count;
                });

                parsedData.push(record);
            });

            this.data.push({ ex_id: String(ex_id), ex_data: parsedData });
        });
    }

    /**
     * Displays tooltip information on hover for a specific data rectangle.
     *
     * @param {ExerciseData} d - The data point for a specific rectangle.
     * @returns {void}
     */
    private tooltipHover(d: ExerciseData): void {
        this.tooltip.transition().duration(200).style("opacity", 0.9);

        let message = "";
        if (this.binStepSize < 24) {
            const onText = i18n.t("js.date_on");
            const timeFormat = d3.timeFormat(i18n.t("time.formats.plain_time"));
            const dateFormat = d3.timeFormat(i18n.t("date.formats.weekday_long"));
            message = `
                <b>${timeFormat(d.date)} - ${timeFormat(new Date(d.date.getTime() + this.binStepSize * 3600000))}</b>
                <br>${onText} ${dateFormat(d.date)}:<br>
            `;
        } else if (this.binStepSize === 24) {
            const format = d3.timeFormat(i18n.t("date.formats.weekday_long"));
            message = `${format(d.date)}:<br>`;
        } else if (this.binStepSize < 168) {
            const format = d3.timeFormat(i18n.t("date.formats.weekday_long"));
            message = `<b>${format(d.date)} - ${format(new Date(d.date.getTime() + this.binStepSize * 3600000))}:</b><br>`;
        } else {
            const weekDay = d3.timeFormat(i18n.t("date.formats.weekday_long"));
            const monthDay = d3.timeFormat(i18n.t("date.formats.monthday_long"));
            message = `<b>${weekDay(d.date)} - ${monthDay(new Date(d.date.getTime() + this.binStepSize * 3600000))}:</b><br>`;
        }
        const subString = d.sum === 1 ? i18n.t("js.submission") : i18n.t("js.submissions");
        message += `<b>${d.sum} ${subString}</b>`;
        this.statusOrder.forEach((status) => {
            if (d[status]) {
                message += `<span style="display: flex; justify-items: center">
                    ${this.submissionStatusIcon(status)}
                    <b style="margin-right: 4px">${d[status]}</b>
                    ${i18n.t(`js.status.${status.replaceAll(" ", "_")}`)}
                </span>`;
            }
        });
        this.tooltip.html(message);
    }

    /**
     * Updates the tooltip position based on mouse movement.
     *
     * @param {unknown} e - The mouse event used to determine the position.
     */
    private tooltipMove(e: unknown): void {
        this.tooltip
            .style("left", `${d3.pointer(e, this.svg.node())[0] + 15}px`)
            .style("top", `${d3.pointer(e, this.svg.node())[1]}px`);
    }

    /**
     * Hides the tooltip when the mouse leaves a rectangle.
     */
    private tooltipOut(): void {
        this.tooltip.transition().duration(200).style("opacity", 0);
    }

    /**
     * Returns an HTML string for a submission status icon.
     *
     * @param {string} status - The submission status.
     * @param {number} [size=18] - The size of the icon.
     * @returns {string} The HTML string representing the icon.
     */
    private submissionStatusIcon(status: string, size: number = 18): string {
        const [icon, color] = {
            correct: ["check", "correct"],
            wrong: ["close", "wrong"],
            "time limit exceeded": ["alarm", "wrong"],
            "runtime error": ["flash", "wrong"],
            "compilation error": ["flash-circle", "wrong"],
            "memory limit exceeded": ["memory", "wrong"],
            "output limit exceeded": ["script-text", "wrong"],
        }[status] || ["alert", "warning"];
        return `<i class="mdi mdi-${icon} mdi-${size} colored-${color}" style="margin-right: 5px"></i>`;
    }

    /**
     * Initializes the graph, including time pickers, and optionally draws the graph.
     *
     * @param {boolean} draw - Whether to draw the graph after initialization.
     * @param {any} [data] - Optional data for initialization.
     */
    public override async init(draw: boolean = true, data = undefined): Promise<void> {
        this.initTimePickers();
        return super.init(draw, data);
    }
}
